import React, { useState, useEffect } from "react";
import { ThreeDots } from "react-loader-spinner";
import Checkbox from "@mui/material/Checkbox";
import { useAuth } from "react-oidc-context";
import styles from "../QuoteMultipleDestinationsPanel.module.css";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import NitroRequestService from "../../../utils/nitroRequest.service";
import { useNitroStore } from "../../../store/Store";
import { createLinkToLocation } from "./RailMissingOriginsTable";
import { calculateStations } from "./multiLocationQuoteUtils";

type BusinessOpportunity = "one-time" | "long-term";
const RailRateRequestPanel: React.FC = () => {
  const [
    railRateRequestContext,
    setDisplayRailRateRequestPanel,
    setRailRateRequestContext,
    addRequestedRailRateRow,
    setShowRailRateRequestFailedPopup,
    enqueueTariffPollRequests,
    triggerExitRequestMultipleMode,
  ] = useNitroCAStore((state) => [
    state.railRateRequestContext,
    state.setDisplayRailRateRequestPanel,
    state.setRailRateRequestContext,
    state.addRequestedRailRateRow,
    state.setShowRailRateRequestFailedPopup,
    state.enqueueTariffPollRequests,
    state.triggerExitRequestMultipleMode,
  ]);

  // TODO: should we default?
  // TODO: make a WAY better loading icon
  const [businessOpportunity, setBusinessOpportunity] =
    useState<BusinessOpportunity>("one-time");
  const [volume, setVolume] = useState("");
  const [targetRate, setTargetRate] = useState("");
  const [sourcingAndLogistics, setSourcingAndLogistics] = useState("");
  const [comments, setComments] = useState("");

  const [selectedOriginNames, setSelectedOriginNames] = useState<Set<string>>(
    new Set(),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const auth = useAuth();
  const [product, products, featureFlags] = useNitroStore((state) => [
    state.product,
    state.products,
    state.featureFlags,
  ]);
  const { multiRailStation } = featureFlags;

  useEffect(() => {
    if (railRateRequestContext) {
      const nonComp = railRateRequestContext.rows
        .filter((r) => !r.quote.competitor)
        .map((r) => r.quote.origin.name);
      setSelectedOriginNames(new Set(nonComp));
    }
  }, [railRateRequestContext]);

  if (!railRateRequestContext) return null;

  const { rows, destination } = railRateRequestContext;
  const rowKeys = rows.map((r) => r.rowKey);
  const nonCompetitorOrigins = rows
    .filter((r) => !r.quote.competitor)
    .map((r) => r.quote.origin);
  const isMultiOrigin = nonCompetitorOrigins.length > 1;

  const close = () => {
    setDisplayRailRateRequestPanel(false);
    setRailRateRequestContext(null);
    setShowRailRateRequestFailedPopup(false);
  };

  const isValid =
    businessOpportunity !== null &&
    volume.trim() !== "" &&
    targetRate.trim() !== "" &&
    sourcingAndLogistics.trim() !== "" &&
    (!isMultiOrigin || selectedOriginNames.size > 0);

  const handleSubmit = async () => {
    // TODO: do we still need other rate request emails?
    // TODO: what emails are needed for rail team
    // TODO: when the users open up multi view then disable other buttons for requested rows maybe? like check??

    triggerExitRequestMultipleMode();
    setIsSubmitting(true);
    const selectedProductName = (product || "").trim();
    const productDetails = products.get(selectedProductName);
    try {
      const result = await NitroRequestService.postRequestForRailRate({
        origins: rows.map((data) => ({
          origin: {
            name: data.quote.origin.name,
            city: `${data.quote.origin.details?.city}, ${data.quote.origin.details?.state}`,
            address: data.quote.origin.details?.address || "",
            zipCode: data.quote.origin.details?.zipCode || "",
            stations: calculateStations(
              multiRailStation,
              data.quote.origin.details,
            ),
            state: data.quote.origin.details?.state || "",
            link: createLinkToLocation(
              window.location.href,
              data.quote.origin.name,
            ),
          },
          competitor: data.quote.competitor,
        })),
        destination: {
          name: destination.name,
          city: `${destination.details?.city}, ${destination.details?.state}`,
          address: destination.details?.address || "",
          zipCode: destination.details?.zipCode || "",
          stations: calculateStations(multiRailStation, destination.details),
          state: destination.details?.state || "",
          link: createLinkToLocation(window.location.href, destination.name),
        },
        rateType: "Tariffs Live",
        stcc: String(productDetails?.stccCodes?.[0] || ""),
        carType: productDetails?.carType || "",
        requestedBy: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
        product: product || "",
        businessOpportunity,
        volume,
        targetRate,
        sourcingAndLogistics,
        comments,
      });
      if (result.status < 200 || result.status >= 300) {
        console.error("postRequestForRailRate failed", result.status, result);
        setShowRailRateRequestFailedPopup(true);
        return;
      }

      // Enqueue rows into the stable TariffPollRunner (mounted in HomePage) so
      // polls survive this panel closing/unmounting.
      enqueueTariffPollRequests(
        rows.map((row) => ({
          rowKey: row.rowKey,
          quote: row.quote,
          destination,
          silent: row.silent,
        })),
      );
      addRequestedRailRateRow(rowKeys);
      close();
    } catch (error) {
      console.error("postRequestForRailRate failed", error);
      setShowRailRateRequestFailedPopup(true);
    } finally {
      setIsSubmitting(false);
    }
    // TODO: trigger reload for fetching rates (will fix rates missing and cancel button)
    // TODO: use mileage requested to persist state
    // TODO: should we do one email per competitor origin
    // TODO: should comp emails have origin or origin city
  };

  // TODO: is multi title ok?
  return (
    <div className={styles.railRateRequestPanelShell}>
      <div className={styles.railRateRequestPanelContainer}>
        {/* Header */}
        <div className={styles.railRateRequestHeader}>
          <div className={styles.railRateRequestHeaderContent}>
            <h2 className={styles.railRateRequestTitle}>Rail Rate Request</h2>
            <div className={styles.railRateRequestRoute}>
              <span className={styles.railRateRequestRouteOrigin}>
                {isMultiOrigin
                  ? "MULTIPLE KOCH ORIGINS"
                  : nonCompetitorOrigins[0]?.name}
              </span>
              <span className={styles.railRateRequestRouteArrow}>→</span>
              <span className={styles.railRateRequestRouteDestination}>
                {destination.name}
              </span>
            </div>
            <p className={styles.railRateRequestIntro}>
              No rail rate was found for this route. Please fill out this form
              to request a rail rate.
            </p>
          </div>
          <button
            type="button"
            className={styles.railRateRequestCloseButton}
            onClick={close}
            disabled={isSubmitting}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Form body */}
        <div className={styles.railRateRequestBody}>
          {/* Origins — only shown in multi-origin mode */}
          {isMultiOrigin && (
            <div className={styles.railRateRequestFieldGroup}>
              <div className={styles.railRateRequestLabelRow}>
                <span className={styles.railRateRequestLabel}>Origins</span>
                <span className={styles.railRateRequestRequired}>*</span>
              </div>
              <p className={styles.railRateRequestHelper}>
                Select what origins you need a rate from
              </p>
              <div className={styles.railRateRequestCheckboxGroup}>
                {nonCompetitorOrigins.map((origin) => (
                  <div
                    key={origin.name}
                    className={styles.railRateRequestCheckboxOption}
                  >
                    <Checkbox
                      checked={selectedOriginNames.has(origin.name)}
                      onChange={() => {
                        setSelectedOriginNames((prev) => {
                          const next = new Set(prev);
                          if (next.has(origin.name)) {
                            next.delete(origin.name);
                          } else {
                            next.add(origin.name);
                          }
                          return next;
                        });
                      }}
                      sx={{
                        padding: "0",
                        color: "#cbd5e1",
                        borderRadius: 0,
                        "&:hover": { backgroundColor: "transparent" },
                        "&.Mui-checked": { color: "#1d4ed8" },
                        "& .MuiSvgIcon-root": { fontSize: 23 },
                      }}
                    />
                    <span className={styles.railRateRequestRadioLabel}>
                      {origin.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Business Opportunity */}
          <div className={styles.railRateRequestFieldGroup}>
            <div className={styles.railRateRequestLabelRow}>
              <span className={styles.railRateRequestLabel}>
                Business Opportunity
              </span>
              <span className={styles.railRateRequestRequired}>*</span>
            </div>
            <p className={styles.railRateRequestHelper}>
              Indicate if this is a one-time or long-term move.
            </p>
            <div className={styles.railRateRequestRadioGroup}>
              <div className={styles.railRateRequestRadioOption}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={businessOpportunity === "one-time"}
                  className={`${styles.railRateRequestRadio} ${
                    businessOpportunity === "one-time"
                      ? styles.railRateRequestRadioSelected
                      : styles.railRateRequestRadioUnselected
                  }`}
                  onClick={() => setBusinessOpportunity("one-time")}
                >
                  {businessOpportunity === "one-time" && (
                    <span className={styles.railRateRequestRadioDot} />
                  )}
                </button>
                <span className={styles.railRateRequestRadioLabel}>
                  One-time move
                </span>
              </div>
              <div className={styles.railRateRequestRadioOption}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={businessOpportunity === "long-term"}
                  className={`${styles.railRateRequestRadio} ${
                    businessOpportunity === "long-term"
                      ? styles.railRateRequestRadioSelected
                      : styles.railRateRequestRadioUnselected
                  }`}
                  onClick={() => setBusinessOpportunity("long-term")}
                >
                  {businessOpportunity === "long-term" && (
                    <span className={styles.railRateRequestRadioDot} />
                  )}
                </button>
                <span className={styles.railRateRequestRadioLabel}>
                  Long-term
                </span>
              </div>
            </div>
          </div>

          {/* Volume */}
          <div className={styles.railRateRequestFieldGroup}>
            <div className={styles.railRateRequestLabelRow}>
              <span className={styles.railRateRequestLabel}>Volume</span>
              <span className={styles.railRateRequestRequired}>*</span>
            </div>
            <p className={styles.railRateRequestHelper}>
              Indicate volume [carloads or tons] on this move. (Wichita - 500st;
              Dodge - 2 carloads)
            </p>
            <input
              className={styles.railRateRequestInput}
              type="text"
              value={volume}
              onChange={(e) => setVolume(e.target.value)}
            />
          </div>

          {/* Target Rate */}
          <div className={styles.railRateRequestFieldGroup}>
            <div className={styles.railRateRequestLabelRow}>
              <span className={styles.railRateRequestLabel}>Target Rate</span>
              <span className={styles.railRateRequestRequired}>*</span>
            </div>
            <p className={styles.railRateRequestHelper}>
              Indicate rate level required to secure the business
            </p>
            <input
              className={styles.railRateRequestInput}
              type="text"
              value={targetRate}
              onChange={(e) => setTargetRate(e.target.value)}
            />
          </div>

          {/* Sourcing and Logistics */}
          <div className={styles.railRateRequestFieldGroup}>
            <div className={styles.railRateRequestLabelRow}>
              <span className={styles.railRateRequestLabel}>
                Sourcing and Logistics
              </span>
              <span className={styles.railRateRequestRequired}>*</span>
            </div>
            <p className={styles.railRateRequestHelper}>
              New Receiver, Current Sourcing, Alternate Transportation Costs,
              etc.
            </p>
            <textarea
              className={styles.railRateRequestTextarea}
              value={sourcingAndLogistics}
              onChange={(e) => setSourcingAndLogistics(e.target.value)}
            />
          </div>

          {/* Comments */}
          <div className={styles.railRateRequestFieldGroup}>
            <div className={styles.railRateRequestLabelRow}>
              <span className={styles.railRateRequestLabel}>Comments</span>
            </div>
            <textarea
              className={styles.railRateRequestTextarea}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>
        </div>

        {/* Actions */}
        <div className={styles.railRateRequestActions}>
          <button
            type="button"
            className={styles.railRateRequestCancelButton}
            onClick={close}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.railRateRequestSubmitButton}
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? (
              <ThreeDots height="16" width="28" color="#ffffff" />
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RailRateRequestPanel;
